/**
 * Which Amazon Seller Central region (NA / EU / FE) a seller most likely belongs to. Only ever a
 * hint for the onboarding region step: nothing is preselected or connected from it.
 */

const NA_COUNTRIES = ['US', 'CA', 'MX', 'BR'];
const FE_COUNTRIES = ['JP', 'AU', 'SG'];

// EU member states Amazon sells to plus the other countries served by the Europe Seller Central
// region (Amazon's "Europe, Middle East, Africa & India" group).
export const EU_MEMBER_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV',
  'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
];
const OTHER_EU_REGION_COUNTRIES = ['GB', 'TR', 'AE', 'SA', 'EG', 'IN', 'ZA', 'CH', 'NO', 'IS', 'LI'];

const REGION_BY_COUNTRY = new Map([
  ...NA_COUNTRIES.map((c) => [c, 'NA']),
  ...FE_COUNTRIES.map((c) => [c, 'FE']),
  ...EU_MEMBER_COUNTRIES.map((c) => [c, 'EU']),
  ...OTHER_EU_REGION_COUNTRIES.map((c) => [c, 'EU']),
]);

// Browser time zones that point to one Amazon store country. Zones shared by several countries
// are left out on purpose, so a wrong guess is never made.
const COUNTRY_BY_TIMEZONE = {
  'America/New_York': 'US',
  'America/Detroit': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Boise': 'US',
  'America/Phoenix': 'US',
  'America/Los_Angeles': 'US',
  'America/Anchorage': 'US',
  'America/Indiana/Indianapolis': 'US',
  'America/Kentucky/Louisville': 'US',
  'Pacific/Honolulu': 'US',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA',
  'America/Regina': 'CA',
  'America/Halifax': 'CA',
  'America/St_Johns': 'CA',
  'America/Mexico_City': 'MX',
  'America/Monterrey': 'MX',
  'America/Cancun': 'MX',
  'America/Tijuana': 'MX',
  'America/Sao_Paulo': 'BR',
  'America/Fortaleza': 'BR',
  'America/Manaus': 'BR',
  'Europe/London': 'GB',
  'Europe/Berlin': 'DE',
  'Europe/Paris': 'FR',
  'Europe/Rome': 'IT',
  'Europe/Madrid': 'ES',
  'Europe/Amsterdam': 'NL',
  'Europe/Stockholm': 'SE',
  'Europe/Warsaw': 'PL',
  'Europe/Brussels': 'BE',
  'Europe/Dublin': 'IE',
  'Europe/Istanbul': 'TR',
  'Asia/Dubai': 'AE',
  'Asia/Riyadh': 'SA',
  'Africa/Cairo': 'EG',
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  'Africa/Johannesburg': 'ZA',
  'Asia/Tokyo': 'JP',
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU',
  'Australia/Adelaide': 'AU',
  'Australia/Hobart': 'AU',
  'Australia/Darwin': 'AU',
  'Asia/Singapore': 'SG',
};

const upper = (value) => String(value || '').trim().toUpperCase();

// 'NA' | 'EU' | 'FE', or null for a country outside Amazon's Seller Central regions.
export const regionForCountry = (countryCode) => REGION_BY_COUNTRY.get(upper(countryCode)) || null;

export const countryForTimeZone = (timeZone) => COUNTRY_BY_TIMEZONE[String(timeZone || '')] || null;

// The first signal that yields a country in an Amazon region wins:
//   1. the country of the business profile's connected Ads profiles
//   2. the browser time zone
// (The sign-up phone is stored as free text with no country code, so it is not a signal.)
// Returns { country, region, source } or null.
export const detectLikelyCountry = ({ adsCountryCodes = [], timeZone = '' } = {}) => {
  for (const code of adsCountryCodes) {
    const country = upper(code);
    const region = regionForCountry(country);
    if (region) return { country, region, source: 'ads-profile' };
  }
  const zoneCountry = countryForTimeZone(timeZone);
  if (zoneCountry) {
    return { country: zoneCountry, region: regionForCountry(zoneCountry), source: 'timezone' };
  }
  return null;
};
